'use client';

import { Badge, Button, Input, Label } from '@commenda/kakashi';
import { Info, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import { SchemaFieldPicker } from '../../editor/SchemaFieldPicker';
import type { FormComponent, ValidationRules } from '@commenda/types';

export function BusinessEntitySelectOrCreateEditor({
  props,
  template,
  onUpdateProps,
  onUpdateTemplate,
  onUpdateLabel,
  onUpdateValidation,
  currentLabel,
  currentValidation,
}: {
  props: Record<string, unknown>;
  template?: FormComponent[];
  onUpdateProps: (key: string, value: unknown) => void;
  onUpdateTemplate: (template: FormComponent[]) => void;
  onUpdateLabel?: (label: string) => void;
  onUpdateValidation?: (validation: FormComponent['validation']) => void;
  currentLabel?: string;
  currentValidation?: FormComponent['validation'];
}) {
  useEffect(() => {
    if (!onUpdateLabel) return;

    const isDefaultLabel =
      !currentLabel ||
      currentLabel === 'Select Entity' ||
      currentLabel === 'New Business Entity Select or Create';

    if (isDefaultLabel) {
      onUpdateLabel('Add Business Entity(s)');
    }
  }, [currentLabel, onUpdateLabel]);

  const handleAddTemplateField = () => {
    const newField: FormComponent = {
      id: `template-field-${Date.now()}`,
      component: 'AutoDetectField',
      fieldId: `field-${Date.now()}`,
      label: 'New Field',
      order: template?.length || 0,
      dataSource: {
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'corporation',
          field: { read: '' },
          mode: 'read-write',
          contextBindings: [],
        },
      },
    };

    onUpdateTemplate([...(template || []), newField]);
  };

  const handleRemoveTemplateField = (fieldId: string) => {
    onUpdateTemplate((template || []).filter((f) => f.id !== fieldId));
  };

  const handleUpdateTemplateField = (
    fieldId: string,
    updates: Partial<FormComponent>
  ) => {
    onUpdateTemplate(
      (template || []).map((f) => (f.id === fieldId ? { ...f, ...updates } : f))
    );
  };

  // Rebuild validation rules when min/max selection changes. `which` says which
  // side the user just touched — the other side is read fresh from props.
  const updateSelectionValidation = (which: 'min' | 'max', value: number) => {
    if (!onUpdateValidation) return;
    const minValue =
      which === 'min' ? value : ((props?.minSelection as number) ?? 1);
    const maxValue =
      which === 'max' ? value : ((props?.maxSelection as number) ?? 1);
    const rules: ValidationRules = { ...currentValidation?.rules };

    if (minValue > 0) {
      rules.required = {
        enabled: true,
        message: `Please select at least ${minValue} business entit${minValue > 1 ? 'ies' : 'y'}`,
      };
      rules.minLength = {
        enabled: true,
        value: minValue,
        message: `Please select at least ${minValue} business entit${minValue > 1 ? 'ies' : 'y'}`,
      };
    } else {
      delete rules.required;
      delete rules.minLength;
    }

    if (maxValue > 0) {
      rules.maxLength = {
        enabled: true,
        value: maxValue,
        message: `Please select no more than ${maxValue} business entit${maxValue > 1 ? 'ies' : 'y'}`,
      };
    } else {
      delete rules.maxLength;
    }

    onUpdateValidation({ rules });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Business Entity Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Required Fields Info */}
        <div className="p-3 bg-bg-brand-subtle rounded-md border border-border-brand">
          <p className="txt-xs-regular text-text-brand">
            <Info className="inline-block w-3 h-3 mr-1" />
            Required fields (automatically included): Legal Name, Country,
            Jurisdiction, Entity Type
          </p>
        </div>

        {/* Display Field (Label) */}
        <div className="space-y-2">
          <Label>Display Field (Label)</Label>
          <SchemaFieldPicker
            entity="corporation"
            value={
              (props?.displayField as string) ||
              'generalCorporationInfo.legalName'
            }
            onSelect={(path) => onUpdateProps('displayField', path)}
            placeholder="Select field for label..."
            readableOnly
          />
          <p className="txt-xs-regular text-text-tertiary">
            The field to display as the option label (default: legal name)
          </p>
        </div>

        {/* Value Field */}
        <div className="space-y-2">
          <Label>Value Field</Label>
          <SchemaFieldPicker
            entity="corporation"
            value={(props?.valueField as string) || 'id'}
            onSelect={(path) => onUpdateProps('valueField', path)}
            placeholder="Select field for value..."
            readableOnly
          />
          <p className="txt-xs-regular text-text-tertiary">
            The field to store as the selected value (usually ID)
          </p>
        </div>

        {/* Selection Limits */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Min Selection</Label>
            <Input
              type="number"
              min={0}
              value={(props?.minSelection as number) ?? 1}
              onChange={(e) => {
                const minValue = parseInt(e.target.value, 10) || 0;
                onUpdateProps('minSelection', minValue);
                updateSelectionValidation('min', minValue);
              }}
            />
          </div>
          <div className="space-y-2">
            <Label>Max Selection</Label>
            <Input
              type="number"
              min={0}
              value={(props?.maxSelection as number) ?? 1}
              onChange={(e) => {
                const maxValue = parseInt(e.target.value, 10) || 0;
                onUpdateProps('maxSelection', maxValue);
                updateSelectionValidation('max', maxValue);
              }}
            />
            <p className="txt-xs-regular text-text-tertiary">0 = unlimited</p>
          </div>
        </div>

        {/* Custom Labels */}
        <div className="space-y-2">
          <Label>Add New Label</Label>
          <Input
            value={(props?.addNewLabel as string) || ''}
            onChange={(e) => onUpdateProps('addNewLabel', e.target.value)}
            placeholder="Add new business entity"
          />
        </div>

        <div className="space-y-2">
          <Label>Select Placeholder</Label>
          <Input
            value={(props?.selectLabel as string) || ''}
            onChange={(e) => onUpdateProps('selectLabel', e.target.value)}
            placeholder="Add or select business entities"
          />
        </div>

        {/* Extra Fields Section */}
        <div className="space-y-3 pt-4 border-t border-border-secondary">
          <div>
            <h4 className="txt-small-medium text-text-secondary">
              Extra Fields
            </h4>
            <p className="txt-xs-regular text-text-tertiary">
              Additional fields to collect per entity (beyond required fields)
            </p>
          </div>

          {/* Template Fields List */}
          {template && template.length > 0 ? (
            <div className="space-y-2">
              {template.map((field, index) => (
                <div
                  key={field.id}
                  className="p-3 bg-bg-secondary rounded-md border border-border-secondary"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="txt-small-medium text-text-secondary">
                      {field.label || `Field ${index + 1}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTemplateField(field.id)}
                      className="p-1 hover:bg-bg-tertiary rounded"
                    >
                      <Trash2 className="w-4 h-4 text-text-tertiary hover:text-text-error" />
                    </button>
                  </div>
                  <SchemaFieldPicker
                    entity="corporation"
                    value={field.dataSource?.commendaOS?.field?.read || ''}
                    onSelect={(path, label) => {
                      handleUpdateTemplateField(field.id, {
                        label: label || path,
                        fieldId: path.replace(/\./g, '-'),
                        dataSource: {
                          type: COMMENDA_OS_DATA_SOURCE,
                          commendaOS: {
                            entity: 'corporation',
                            field: { read: path },
                            mode: 'read-write',
                            contextBindings: [],
                          },
                        },
                      });
                    }}
                    placeholder="Select field..."
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 border border-dashed border-border-secondary rounded-md text-center">
              <p className="txt-small-regular text-text-tertiary">
                No extra fields configured. Required fields (Legal Name,
                Country, Jurisdiction, Entity Type) are always included.
              </p>
            </div>
          )}

          {/* Add Field Button */}
          <Button
            variant="tertiary"
            size="sm"
            onClick={handleAddTemplateField}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Extra Field
          </Button>
        </div>
      </div>
    </div>
  );
}
